export interface GeofenceCandidate {
    id: string
    job: string
    org: string
    latitude: number
    longitude: number
    radius: number
}

export interface FixForMatch {
    lat: number
    lng: number
    accuracy: number | null
}

const EARTH_RADIUS_METERS = 6_371_000

function haversineMeters(lat1: number, lng1: number, lat2: number, lng2: number): number {
    const toRad = (deg: number) => (deg * Math.PI) / 180
    const dLat = toRad(lat2 - lat1)
    const dLng = toRad(lng2 - lng1)
    const a =
        Math.sin(dLat / 2) ** 2 +
        Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2
    return 2 * EARTH_RADIUS_METERS * Math.asin(Math.sqrt(a))
}

/**
 * Returns the most specific geofence the given fix is inside, or null.
 *
 * Specificity rule: of the candidates that contain the fix, prefer the one
 * with the smallest radius (most precise overlay); tie-break on smallest
 * distance from the fix to the geofence center.
 *
 * Accuracy floor: a fix whose accuracy radius exceeds the candidate's
 * radius is rejected for that candidate. A 200-meter accuracy fix matching
 * a 50-meter geofence is too imprecise to act on.
 */
export function pickContainingGeofence(
    fix: FixForMatch,
    candidates: GeofenceCandidate[]
): GeofenceCandidate | null {
    let best: { candidate: GeofenceCandidate; distance: number } | null = null

    for (const candidate of candidates) {
        if (!candidate.radius || candidate.radius <= 0) continue
        if (fix.accuracy !== null && fix.accuracy > candidate.radius) continue

        const distance = haversineMeters(fix.lat, fix.lng, candidate.latitude, candidate.longitude)
        if (distance > candidate.radius) continue

        if (
            best === null ||
            candidate.radius < best.candidate.radius ||
            (candidate.radius === best.candidate.radius && distance < best.distance)
        ) {
            best = { candidate, distance }
        }
    }

    return best?.candidate ?? null
}
